import type { PartnerClient } from '@epilot/sdk/identity'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { EPILOT_IDP_PATH } from './shared'

import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'

/**
 * Retired applications, kept out of the main table: nobody works with them any more, but they stay
 * listed because a client ID is never reused. Collapsed by default, so they cost a single line.
 */
export const RetiredApplications = ({
  clients
}: {
  clients: PartnerClient[]
}) => {
  const [open, setOpen] = useState(false)

  if (!clients.length) return null

  // Nothing can change a retired record, so its last update is the moment it was retired.
  const newestFirst = [...clients].sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  )

  return (
    <Collapsible
      className="px-4 py-2 space-y-2 border border-border rounded-md"
      onOpenChange={setOpen}
      open={open}
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between cursor-pointer">
          <h4 className="text-sm font-semibold text-primary">
            Retired applications ({clients.length})
          </h4>
          <Button className="p-0 w-9" size="sm" type="button" variant="ghost">
            {open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="sr-only">Toggle retired applications</span>
          </Button>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Retired applications can no longer sign anyone in and cannot be
          activated again. Their client IDs are never reused.
        </p>
        <Table className="text-primary">
          <TableHeader>
            <TableRow>
              <TableHead>Application</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Retired</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {newestFirst.map((client) => (
              <TableRow key={client.client_id}>
                <TableCell>
                  <Link
                    className="underline"
                    to={`${EPILOT_IDP_PATH}/${encodeURIComponent(
                      client.client_id
                    )}`}
                  >
                    {client.display_name}
                  </Link>
                </TableCell>
                <TableCell>{client.client_id}</TableCell>
                <TableCell>{client.partner_key}</TableCell>
                <TableCell>
                  {new Date(client.updated_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  )
}
